import type { Msg } from "../../../types/chat";
import type { NotifyArgs } from "../../notifications/useInPageNotification";
import { applyIncoming, classifyIncoming, emptyIncomingCache, type IncomingCache } from "../messageList";
import { planDuplicateContent, planIncoming } from "../incomingMessage";

// Module copy of ChatLayout's pure key helpers (same pattern as useFavorites / useAndroidShare).
const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);
const k = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);

type MsgsByKey = Record<string, Msg[]>;

type Options<OutboxItem extends { tempId?: string }> = {
  /** The space and channel on screen. */
  currentVoidId: string;
  currentChannelId: string;
  /** The signed-in user's id (empty before sign-in). */
  userId?: string;
  channels: { id: string; name?: string }[];
  mutedSpaces: Record<string, boolean>;
  hiddenSpaces: Record<string, boolean>;
  favorites: string[];
  /** What we remember about recent deliveries per channel, to drop duplicates. */
  recentIncomingRef: { current: Record<string, IncomingCache> };
  /** The scrolling message list, and the favourite cards on the home screen. */
  listRef: { current: { scrollHeight: number; scrollTop: number; clientHeight: number } | null };
  favScrollRefs: { current: Record<string, { scrollTop: number; scrollHeight: number } | null> };
  notifiedRef: NotifyArgs["notifiedRef"];
  seenIdsRef: NotifyArgs["seenIdsRef"];
  setMsgsByKey: (updater: (old: MsgsByKey) => MsgsByKey) => void;
  setChatInContextMode: (value: boolean) => void;
  setOutbox: (updater: (prev: OutboxItem[]) => OutboxItem[]) => void;
  setUnreadMarkerByChan: (updater: (prev: Record<string, string | null>) => Record<string, string | null>) => void;
  setNotified: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  stripSelfSeen: (m: Msg) => Msg;
  markUnreadTracked: (fid: string) => void;
  markChannelRead: (voidId: string, chanId: string, lastMessageId?: string) => void;
  scrollToBottom: () => void;
  handleNotification: (args: NotifyArgs) => void;
  addPreview: NotifyArgs["addPreview"];
  refreshPreview: NotifyArgs["refreshPreview"];
  openChannelFromFqid: (fqid: string, fallbackVoidId?: string) => void;
};

/**
 * What happens when a new message arrives over the socket (and when the server acknowledges one we sent).
 * In order: drop duplicate deliveries; add it to the list (replacing our own optimistic copy when it is the
 * echo of our send); leave search "context mode"; drop it from the offline queue; mark the channel read if
 * it is on screen and the tab is visible; keep a DM scrolled to the newest message; place the "New" divider
 * if the list is scrolled up; raise a notification if it is for another channel; scroll a favourite card.
 * The decisions are in `incomingMessage.ts`; this runs the actions in the same order as before.
 *
 * Returns the handlers, built fresh each render (so each sees the current state); ChatLayout registers
 * them with the rest of the socket events. Moved out of ChatLayout with the same behaviour.
 */
export function useIncomingMessageHandlers<OutboxItem extends { tempId?: string } = { tempId?: string }>({
  currentVoidId,
  currentChannelId,
  userId,
  channels,
  mutedSpaces,
  hiddenSpaces,
  favorites,
  recentIncomingRef,
  listRef,
  favScrollRefs,
  notifiedRef,
  seenIdsRef,
  setMsgsByKey,
  setChatInContextMode,
  setOutbox,
  setUnreadMarkerByChan,
  setNotified,
  stripSelfSeen,
  markUnreadTracked,
  markChannelRead,
  scrollToBottom,
  handleNotification,
  addPreview,
  refreshPreview,
  openChannelFromFqid,
}: Options<OutboxItem>) {
  const onNew = ({ voidId, channelId, message, tempId }: { voidId: string; channelId: string; message: Msg; tempId?: string }) => {
    const kk = k(voidId, channelId);
    const msgId = message?.id;
    const authorId = message?.authorId || '';
    // Drop duplicate deliveries (same id, or same author+content within 5s)
    const cache = recentIncomingRef.current[kk] || emptyIncomingCache();
    recentIncomingRef.current[kk] = cache;
    const verdict = classifyIncoming(cache, { msgId, authorId, content: message?.content, kind: message.kind, tempId, now: Date.now() });
    if (verdict === 'duplicate-id') return;
    if (verdict === 'duplicate-content') {
      // The content-dedup only prevents a duplicate bubble in the UI.
      // We still need to count it as unread so the badge increments correctly.
      const unreadFor = planDuplicateContent({ voidId, channelId, currentVoidId, currentChannelId, authorId: message.authorId, meUserId: userId });
      if (unreadFor) markUnreadTracked(unreadFor);
      return;
    }
    const plan = planIncoming({ voidId, channelId, currentVoidId, currentChannelId, tempId, isVisible: document.visibilityState === 'visible', favorites });
    setMsgsByKey((old) => {
      const list = old[kk] ?? [];
      const next = applyIncoming(list, message, { tempId, isCurrentChannel: plan.isCurrentChannel, stripSelfSeen });
      return next === list ? old : { ...old, [kk]: next };
    });
    // If a live message arrives on the current channel while in context mode, exit context mode
    if (plan.exitContextMode) {
      setChatInContextMode(false);
    }
    // Drop from outbox once server acknowledged this tempId
    if (plan.clearOutboxFor) {
      const acknowledged = plan.clearOutboxFor;
      setOutbox(prev => prev.filter(it => it.tempId !== acknowledged));
    }
    // If we are viewing this channel and window is visible, mark seen up to this message
    if (plan.markRead) {
      markChannelRead(voidId, channelId, message.id);
    }
    // In DMs, always keep the newest message visible
    if (plan.scrollDmToBottom) {
      requestAnimationFrame(() => scrollToBottom());
      setTimeout(() => scrollToBottom(), 50);
    }
    // If user is scrolled up, remember first unseen message id for divider
    if (plan.checkUnreadDivider) {
      const el = listRef.current;
      if (el) {
        const nearBottom = (el.scrollHeight - el.scrollTop - el.clientHeight) < 40;
        if (!nearBottom) {
          const cid = fq(voidId, channelId);
          setUnreadMarkerByChan(prev => prev[cid] ? prev : ({ ...prev, [cid]: message.id }));
        }
      }
    }
    // If not the active channel, surface notification via hook
    if (plan.notify) {
      const msgFqid = channelId.includes(':') ? channelId : fq(voidId, channelId);
      handleNotification({
        voidId,
        channelId,
        channelName: channels.find((c) => fq(voidId, c.id) === msgFqid)?.name,
        authorId: message.authorId,
        authorName: message.authorName,
        content: message.content,
        messageId: message.id,
        pushSent: undefined,
        isMine: !!(message.authorId && userId && message.authorId === userId),
        isCurrentChannel: false,
        mutedSpaces,
        hiddenSpaces,
        favorites,
        addPreview,
        refreshPreview,
        markUnread: markUnreadTracked,
        onClickChannel: (vid, cid) => {
          const target = cid.includes(':') ? cid : fq(vid, cid);
          openChannelFromFqid(target, vid);
        },
        notifiedRef,
        seenIdsRef,
        setNotified: (fid: string) => setNotified((prev) => ({ ...prev, [fid]: true })),
      });
    }
    if (plan.favoriteKey) {
      const favKey = plan.favoriteKey;
      setTimeout(() => {
        const el = favScrollRefs.current[favKey];
        try { if (el) el.scrollTop = el.scrollHeight; } catch {}
      }, 0);
    }
  };

  // The server's acknowledgement of a message we sent carries the same payload as a new message.
  const onMessageAck = (payload: { voidId: string; channelId: string; message: Msg; tempId?: string }) => {
    if (!payload?.tempId) return;
    onNew(payload);
  };

  return { onNew, onMessageAck };
}

